import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";

import { uuid } from "../../utils/uuid";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DraftsRoundedIcon from "@mui/icons-material/DraftsRounded";
import EditCalendarRoundedIcon from "@mui/icons-material/EditCalendarRounded";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";

import { useAuth } from "../../hooks/useAuth";
import { ref, get } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";
import { updateEventRtdb } from "../../services/events/eventsService";

import GlassCard from "./novoEvento/components/GlassCard";
import StepperRail from "./novoEvento/components/StepperRail";
import StepHeader from "./novoEvento/components/StepHeader";
import StepFooter from "./novoEvento/components/StepFooter";
import RightPreview from "./novoEvento/components/RightPreview";

import { useNovoEvento } from "./novoEvento/useNovoEvento";
import { validateStep, hasErrors } from "./novoEvento/utils/validators";

import IdentidadeStep from "./novoEvento/components/steps/IdentidadeStep";
import DataLocalStep from "./novoEvento/components/steps/DataLocalStep";
import DescricaoStep from "./novoEvento/components/steps/DescricaoStep";
import IngressosStep from "./novoEvento/components/steps/IngressosStep";
import VendasStep from "./novoEvento/components/steps/VendasStep";
import RegrasStep from "./novoEvento/components/steps/RegrasStep";
import RevisaoStep from "./novoEvento/components/steps/RevisaoStep";

const STEPS = [
  {
    key: "identidade",
    label: "Identidade",
    short: "Nome, categoria e banner",
    helper:
      "Atualize o título, categoria, classificação e a identidade visual do evento.",
  },
  {
    key: "dataLocal",
    label: "Data & Local",
    short: "Quando e onde",
    helper:
      "Revise data, horário, abertura dos portões e endereço para manter a comunicação precisa.",
  },
  {
    key: "descricao",
    label: "Descrição",
    short: "Storytelling do evento",
    helper:
      "Ajuste a descrição, line-up e observações para manter a página forte e vendável.",
  },
  {
    key: "ingressos",
    label: "Ingressos",
    short: "Lotes e setores",
    helper:
      "Edite lotes, preços, quantidade e janelas de venda sem perder a organização do fluxo.",
  },
  {
    key: "vendas",
    label: "Vendas & Comissários",
    short: "Operação comercial",
    helper:
      "Atualize limites, taxa, pontos físicos e comissários oficiais do evento.",
  },
  {
    key: "regras",
    label: "Regras",
    short: "Políticas do evento",
    helper:
      "Ajuste menores, reembolso e avisos importantes com a mesma experiência do novo evento.",
  },
  {
    key: "revisao",
    label: "Revisão",
    short: "Checklist final",
    helper:
      "Confira tudo antes de salvar as alterações ou republicar o evento.",
  },
];

function fallbackId() {
  return uuid();
}

function msToDate(ms) {
  if (!ms) return "";
  const d = new Date(ms);
  return d.toISOString().slice(0, 10);
}

function msToTime(ms) {
  if (!ms) return "";
  const d = new Date(ms);
  return d.toTimeString().slice(0, 5);
}

function rtdbToFormState(
  event,
  tags,
  lineup,
  ticketTypes,
  ticketLots,
  commissaries,
  physicalPoints,
) {
  const gatesOpenText = event.gatesOpenText || "";
  const aberturaPortoes = gatesOpenText
    .replace("Abertura dos portões às ", "")
    .trim();

  const tagsArr = tags
    ? Object.values(tags)
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map((t) => t.label)
    : [];

  const lineupArr = lineup
    ? Object.values(lineup)
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map((l) => l.label)
    : [];

  const ingressosArr = ticketTypes
    ? Object.values(ticketTypes)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map((tt) => {
          const lotId = `${tt.ticketTypeId}_lot_1`;
          const lot = ticketLots?.[lotId] || {};

          return {
            id: tt.ticketTypeId,
            nome: tt.name || "",
            lote: lot.name || "Lote 1",
            preco: lot.price ?? 0,
            quantidade: lot.quantity ?? 0,
            vendas: {
              inicio: msToDate(lot.salesStartMs),
              fim: msToDate(lot.salesEndMs),
            },
            taxaAbsorvidaOrganizador: !!tt.feeAbsorbedByOrganizer,
            visibilidade: tt.visibility || "publico",
          };
        })
    : [];

  const comissariosArr = commissaries
    ? Object.values(commissaries).map((c) => ({
        id: c.id || fallbackId(),
        nome: c.name || "",
        phone: c.phone || "",
      }))
    : [{ id: fallbackId(), nome: "", phone: "" }];

  const pontosFisicosArr = physicalPoints
    ? Object.values(physicalPoints)
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map((p) => p.name || "")
    : [];

  return {
    status: event.status || "draft",

    identidade: {
      titulo: event.title || "",
      categoria: event.category || "",
      classificacao: event.classification || "18",
      tags: tagsArr,
      banner: {
        mode: "url",
        url: event.bannerUrl || "",
        file: null,
        previewUrl: event.bannerUrl || "",
      },
    },

    dataLocal: {
      dataInicio: msToDate(event.timing?.startAtMs),
      horaInicio: msToTime(event.timing?.startAtMs),
      dataFim: msToDate(event.timing?.endAtMs),
      horaFim: msToTime(event.timing?.endAtMs),
      aberturaPortoes,
      local: {
        nome: event.location?.name || "",
        endereco: event.location?.address || "",
        cidade: event.location?.city || "",
        estado: event.location?.state || "",
        referencia: event.location?.reference || "",
        mapsUrl: event.location?.mapsUrl || "",
      },
    },

    descricao: {
      texto: event.description || "",
      lineup: lineupArr,
      observacoes: event.notes || "",
    },

    ingressos:
      ingressosArr.length > 0
        ? ingressosArr
        : [
            {
              id: fallbackId(),
              nome: "",
              lote: "Lote 1",
              preco: 0,
              quantidade: 0,
              vendas: { inicio: "", fim: "" },
              taxaAbsorvidaOrganizador: false,
              visibilidade: "publico",
            },
          ],

    vendas: {
      pontosFisicos: pontosFisicosArr,
      comissarios: comissariosArr,
      limitePorUsuario: event.salesSettings?.limitPerUser ?? 2,
      taxaOnurbPercent: event.salesSettings?.onurbFeePercent ?? 3,
    },

    regras: {
      permiteMenores: !!event.rules?.allowMinors,
      detalhesMenores: event.rules?.minorsDetails || "",
      politicaReembolso: event.rules?.refundPolicy || "padrao",
      reembolsoPersonalizado: event.rules?.refundCustomText || "",
      avisoImportante: event.rules?.importantNotice || "",
    },

    seo: {
      slug: event.seo?.slug || "",
    },
  };
}

export default function EventEditor() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const organizerId = user?.uid || "";

  const [loadedState, setLoadedState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!eventId) {
      setLoadError("ID do evento não informado.");
      setLoading(false);
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        setLoading(true);
        setLoadError("");

        const [
          eventSnap,
          tagsSnap,
          lineupSnap,
          ttSnap,
          lotsSnap,
          commSnap,
          pointsSnap,
        ] = await Promise.all([
          get(ref(rtdb, `events/${eventId}`)),
          get(ref(rtdb, `eventTags/${eventId}`)),
          get(ref(rtdb, `eventLineup/${eventId}`)),
          get(ref(rtdb, `ticketTypes/${eventId}`)),
          get(ref(rtdb, `ticketLots/${eventId}`)),
          get(ref(rtdb, `eventCommissaries/${eventId}`)),
          get(ref(rtdb, `eventPhysicalPoints/${eventId}`)),
        ]);

        if (!eventSnap.exists()) {
          if (isMounted) setLoadError("Evento não encontrado.");
          return;
        }

        const formState = rtdbToFormState(
          eventSnap.val(),
          tagsSnap.val(),
          lineupSnap.val(),
          ttSnap.val(),
          lotsSnap.val(),
          commSnap.val(),
          pointsSnap.val(),
        );

        if (isMounted) setLoadedState(formState);
      } catch (err) {
        console.error(err);
        if (isMounted) setLoadError("Erro ao carregar evento.");
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [eventId]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "grid",
          placeItems: "center",
          px: 2,
        }}
      >
        <GlassCard sx={{ p: 3, width: "100%", maxWidth: 520 }}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <CircularProgress />
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 0.4 }}>
                Carregando dados do evento
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>
                Estamos puxando as informações já salvas para abrir o editor com
                tudo preenchido.
              </Typography>
            </Box>
          </Stack>
        </GlassCard>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ py: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ borderRadius: 1 }}>
            {loadError}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <EventEditorForm
      eventId={eventId}
      organizerId={organizerId}
      initialState={loadedState}
    />
  );
}

function EventEditorForm({ eventId, organizerId, initialState }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { state, actions } = useNovoEvento(initialState);

  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [savedMode, setSavedMode] = useState("draft");

  const CurrentStep = useMemo(() => {
    if (step === 0) return IdentidadeStep;
    if (step === 1) return DataLocalStep;
    if (step === 2) return DescricaoStep;
    if (step === 3) return IngressosStep;
    if (step === 4) return VendasStep;
    if (step === 5) return RegrasStep;
    return RevisaoStep;
  }, [step]);

  const currentMeta = STEPS[step] || STEPS[0];
  const progress = ((step + 1) / STEPS.length) * 100;

  function goNext() {
    const nextErrors = validateStep(step, state);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) return;
    setStep((prev) => Math.min(STEPS.length - 1, prev + 1));
    setSubmitError("");
  }

  function goBack() {
    setErrors({});
    setSubmitError("");
    setStep((prev) => Math.max(0, prev - 1));
  }

  async function handleSave(status) {
    try {
      setSaving(true);
      setSubmitError("");

      await updateEventRtdb(eventId, state, {
        status,
        organizerId,
        createdByUid: user?.uid || "",
      });

      setSavedMode(status);
      setSavedOk(true);
    } catch (err) {
      console.error(err);
      setSubmitError("Não consegui salvar as alterações. Veja o console.");
    } finally {
      setSaving(false);
    }
  }

  async function onSubmit() {
    const nextErrors = validateStep(step, state);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) return;
    await handleSave("online");
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={2.5}>
          <GlassCard sx={{ p: { xs: 2, md: 3 }, overflow: "hidden" }}>
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: "column", lg: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", lg: "center" }}
                gap={2}
              >
                <Stack spacing={1.2} sx={{ maxWidth: 780 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Chip
                      icon={<EditCalendarRoundedIcon sx={{ fontSize: 18 }} />}
                      label="Editor premium de evento"
                      sx={{
                        fontWeight: 800,
                        bgcolor: "rgba(0,225,255,0.12)",
                        color: "#8ef4ff",
                        border: "1px solid rgba(0,225,255,0.22)",
                      }}
                    />
                    <Chip
                      icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} />}
                      label={`Etapa ${step + 1} de ${STEPS.length}`}
                      sx={{
                        fontWeight: 800,
                        bgcolor: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    />
                    <Chip
                      label={`ID: ${eventId}`}
                      sx={{
                        fontWeight: 800,
                        bgcolor: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    />
                  </Stack>

                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 950,
                        letterSpacing: -1,
                        lineHeight: 1.05,
                        mb: 0.8,
                      }}
                    >
                      Edite seu evento com a mesma experiência bonita, fluida e
                      intuitiva do novo evento.
                    </Typography>
                    <Typography
                      sx={{ color: "rgba(255,255,255,0.72)", maxWidth: 760 }}
                    >
                      Os dados já entram preenchidos a partir do evento salvo.
                      Aqui você só ajusta o que precisar, revisa por etapas e
                      salva tudo sem perder o fluxo visual mais premium.
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.2}
                  sx={{ width: { xs: "100%", lg: "auto" } }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<KeyboardBackspaceRoundedIcon />}
                    disabled={saving}
                    onClick={() => navigate("/dashboard/eventos")}
                    sx={{
                      minHeight: 46,
                      px: 2,
                      borderRadius: 2,
                      fontWeight: 900,
                      borderColor: "rgba(255,255,255,0.14)",
                    }}
                  >
                    Voltar
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<DraftsRoundedIcon />}
                    disabled={saving}
                    onClick={() => handleSave("draft")}
                    sx={{
                      minHeight: 46,
                      px: 2.2,
                      borderRadius: 2,
                      fontWeight: 900,
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.08))",
                      color: "#fff",
                      boxShadow: "none",
                      border: "1px solid rgba(255,255,255,0.12)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.1))",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {saving ? "Salvando..." : "Salvar alterações"}
                  </Button>
                </Stack>
              </Stack>

              <Stack spacing={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 13,
                      color: "rgba(255,255,255,0.68)",
                    }}
                  >
                    Progresso da edição
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: 13,
                      color: "rgba(255,255,255,0.9)",
                    }}
                  >
                    {Math.round(progress)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 10,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.08)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 2,
                      background:
                        "linear-gradient(90deg, rgba(0,225,255,0.95), rgba(109,255,178,0.95))",
                    },
                  }}
                />
                <Typography
                  sx={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}
                >
                  Agora você está em <strong>{currentMeta.label}</strong>:{" "}
                  {currentMeta.short}.
                </Typography>
              </Stack>
            </Stack>
          </GlassCard>

          {submitError ? (
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: "rgba(255,107,107,0.4)",
                bgcolor: "rgba(255,107,107,0.08)",
              }}
            >
              {submitError}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1.15fr) 420px" },
              gap: 2.5,
              alignItems: "start",
            }}
          >
            <GlassCard sx={{ p: { xs: 1.5, md: 2 } }}>
              <Stack spacing={2}>
                <StepHeader
                  steps={STEPS}
                  step={step}
                  title={currentMeta.label}
                  helper={currentMeta.helper}
                />

                <StepperRail
                  steps={STEPS}
                  step={step}
                  onStepClick={(i) => setStep(i)}
                />

                <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

                <Box sx={{ minHeight: { md: 520 } }}>
                  <CurrentStep
                    state={state}
                    actions={actions}
                    errors={errors}
                  />
                </Box>

                <StepFooter
                  step={step}
                  lastStep={STEPS.length - 1}
                  onBack={goBack}
                  onNext={goNext}
                  onSubmit={onSubmit}
                  nextLabel={STEPS[Math.min(step + 1, STEPS.length - 1)]?.label}
                  saving={saving}
                />
              </Stack>
            </GlassCard>

            <RightPreview state={state} />
          </Box>
        </Stack>
      </Container>

      <Dialog
        open={savedOk}
        onClose={() => setSavedOk(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(7,12,24,0.94)",
            backdropFilter: "blur(20px)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 950,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircleRoundedIcon sx={{ color: "#6dffb2" }} />
          {savedMode === "online"
            ? "Evento atualizado e publicado"
            : "Alterações salvas"}
        </DialogTitle>
        <DialogContent sx={{ color: "rgba(255,255,255,0.74)" }}>
          {savedMode === "online"
            ? "As alterações foram salvas e o evento continua online com os dados atualizados."
            : "As alterações foram salvas como rascunho para você continuar editando quando quiser."}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.4 }}>
          <Button
            variant="contained"
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              background:
                "linear-gradient(135deg, rgba(0,225,255,0.95), rgba(109,255,178,0.95))",
              color: "#052018",
            }}
            onClick={() => setSavedOk(false)}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
